import { useEffect, useState } from "react";
import { galleryItems } from "@/mocks/gossip";

export default function Gallery() {
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const cur = active === null ? null : galleryItems[active];

  return (
    <section id="gallery" className="w-full bg-[rgb(var(--paper-deep))]/60">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="mb-6 flex flex-col justify-between gap-3 md:mb-8 md:flex-row md:items-end">
          <div>
            <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold tracking-[0.25em] text-[rgb(var(--brand))]">
              <i className="ri-image-line h-4 w-4 flex items-center justify-center"></i>
              图文并重 · 现场素材
            </div>
            <h2 className="font-display text-[24px] font-black leading-tight md:text-[30px]">
              实锤图鉴，一图胜过千言
            </h2>
            <p className="mt-1 text-[13px] text-[rgb(var(--ink-soft))]">
              聊天记录、现场抓拍与后台素材按事件归档，点开可看大图与来源说明
            </p>
          </div>
          <a
            href="#comments"
            className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-medium text-[rgb(var(--brand))] transition-colors hover:text-[rgb(var(--brand-deep))]"
          >
            前往围观区
            <i className="ri-arrow-right-line h-4 w-4 flex items-center justify-center"></i>
          </a>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {galleryItems.map((g, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className={`group relative overflow-hidden rounded-lg border border-[rgb(var(--ink))]/10 ${
                i === 0 ? "col-span-2 row-span-2 sm:col-span-2 sm:row-span-2" : ""
              }`}
            >
              <img
                src={g.image}
                alt={g.caption}
                className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                  i === 0 ? "aspect-auto min-h-full" : "aspect-[4/5]"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent opacity-90"></div>
              <span className="absolute left-2.5 top-2.5 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
                {g.tag}
              </span>
              <div className="absolute bottom-2 left-3 right-3 text-left">
                <p className="truncate text-[12px] font-medium text-white">{g.caption}</p>
              </div>
              <span className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                <i className="ri-zoom-in-line h-4 w-4 flex items-center justify-center"></i>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 灯箱 */}
      {cur && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setActive(null)}
        >
          <div
            className="animate-rise w-full max-w-3xl overflow-hidden rounded-lg bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={cur.image}
                alt={cur.caption}
                className="max-h-[60vh] w-full object-cover"
              />
              <button
                type="button"
                onClick={() => setActive(null)}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75"
                aria-label="关闭"
              >
                <i className="ri-close-line h-5 w-5 flex items-center justify-center"></i>
              </button>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="rounded-md bg-[rgb(var(--brand))]/10 px-2 py-1 text-[12px] font-semibold text-[rgb(var(--brand))]">
                  {cur.tag}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[rgb(var(--ink))]/15 text-[rgb(var(--ink-soft))] hover:text-[rgb(var(--brand))]"
                    aria-label="收藏"
                  >
                    <i className="ri-heart-3-line h-4 w-4 flex items-center justify-center"></i>
                  </button>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[rgb(var(--ink))]/15 text-[rgb(var(--ink-soft))] hover:text-[rgb(var(--brand))]"
                    aria-label="保存"
                  >
                    <i className="ri-download-2-line h-4 w-4 flex items-center justify-center"></i>
                  </button>
                  <button
                    type="button"
                    className="flex h-8 items-center gap-1 rounded-md border border-[rgb(var(--ink))]/15 px-2.5 text-[12px] text-[rgb(var(--ink-soft))] hover:text-[rgb(var(--brand))]"
                  >
                    <i className="ri-share-forward-line h-4 w-4 flex items-center justify-center"></i>
                    转发
                  </button>
                </div>
              </div>
              <p className="font-display mt-2 text-[16px] font-bold">{cur.caption}</p>
              <p className="mt-1 text-[12px] text-[rgb(var(--ink-soft))]">
                素材来源：全网公开流传整理 · 仅作事件复盘展示
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}